import { Checkbox, Flex, Text, Input, IconButton } from "@chakra-ui/react";
import { RootState, useAppDispatch, useAppSelector } from "../../store";
import { fetchAnaliseVacancy, setPeriodFrom, setPeriodTo } from "../../store/slices/analytics";
import { useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import { ru } from "date-fns/locale";
import { CloseIcon } from "@chakra-ui/icons";
import "react-datepicker/dist/react-datepicker.css";
import "./DateRangePicker.css";

registerLocale("ru", ru);

function formatTimestamp(timestamp: number | null) {
  if (timestamp) {
    const date = new Date(timestamp * 1000);
    return date;
  }
  return null;
}

interface DateRangePickerProps {
  withCheckbox?: boolean
}

function DateRangePicker({ withCheckbox = false }: DateRangePickerProps) {
  const { period_from, period_to } = useAppSelector(
    (state: RootState) => state.analytics
  );

  const { id } = useParams();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  const handleCheckboxChange = useCallback((e: any) => {
    setIsChecked(e.target.checked);
    if (e.target.checked) {
      dispatch(fetchAnaliseVacancy({ period_from: null, period_to: null, vacancy_id: Number(id) }));
    } else {
      dispatch(fetchAnaliseVacancy({ period_from: period_from, period_to: period_to, vacancy_id: Number(id) }));
    }
  }, [dispatch, id, period_from, period_to]);

  const handleDateChange = useCallback((dates: [Date | null, Date | null]) => {
    const [start, end] = dates;
    if (start) {
      dispatch(setPeriodFrom(Math.floor(start.getTime() / 1000)));
    } else {
      dispatch(setPeriodFrom(null));
    }
    if (end) {
      dispatch(setPeriodTo(Math.floor(end.getTime() / 1000)));
    } else {
      dispatch(setPeriodTo(null));
    }
  }, [dispatch]);

  const handleReset = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    dispatch(setPeriodFrom(null));
    dispatch(setPeriodTo(null));
    if (id) {
      dispatch(fetchAnaliseVacancy({ period_from: null, period_to: null, vacancy_id: Number(id) }));
    }
  }, [dispatch, id]);

  const customInput = (
    <Flex position="relative" width="250px">
      <Input
        readOnly
        value={period_from || period_to ? `${formatTimestamp(period_from)?.toLocaleDateString('ru-RU') || ''} - ${formatTimestamp(period_to)?.toLocaleDateString('ru-RU') || ''}` : ''}
        placeholder="Выберите период"
        width="100%"
        cursor="pointer"
        isDisabled={isChecked}
        border="1px solid #e1e7ef"
        _hover={{ border: "1px solid #e1e7ef" }}
        _focus={{ border: "1px solid #e1e7ef" }}
        pointerEvents="none"
        height="38px"
      />
      {(period_from || period_to) && (
        <IconButton
          aria-label="Сбросить период"
          icon={<CloseIcon />}
          size="sm"
          position="absolute"
          right="2"
          top="50%"
          transform="translateY(-50%)"
          onClick={handleReset}
          variant="ghost"
          isDisabled={isChecked}
          pointerEvents="auto"
        />
      )}
    </Flex>
  );

  return (
    <Flex flexDirection="column">
      <Flex alignItems="center" gap="10px">
        <Text margin="0">За период:</Text>
        <Flex position="relative">
          <DatePicker
            selected={formatTimestamp(period_from)}
            onChange={handleDateChange}
            startDate={formatTimestamp(period_from)}
            endDate={formatTimestamp(period_to)}
            selectsRange
            customInput={customInput}
            disabled={isChecked}
            dateFormat="dd.MM.yyyy"
            locale="ru"
            monthsShown={1}
            popperClassName="react-datepicker-popper"
            popperPlacement="bottom-start"
            portalId="root"
          />
        </Flex>
      </Flex>
      {withCheckbox && (
        <Checkbox isChecked={isChecked} onChange={handleCheckboxChange}>
          Показать всех кандидатов
        </Checkbox>
      )}
    </Flex>
  );
}

export default DateRangePicker;
